import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import activeTicketService from '../../../services/activeTicketsService';
import serviceTypeService from '../../../services/serviceTypeService';
import AuthService from '../../../services/authService';
import { WS_STATUS, useWebSocket } from '../../../common/hooks/useWebSocket';

const QUEUE_TOPIC = '/topic/queue-events';
// Безусловный периодический ре-синк — страховка от «тихо умершего» WS
const RESYNC_INTERVAL_MS = 30_000;

export { WS_STATUS };

export const useManagerCabinet = () => {
	const [errorMessage, setErrorMessage] = useState('');
	const [infoMessage, setInfoMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const [tickets, setTickets] = useState([]);
	const [userInfo, setUserInfo] = useState({});
	const [serviceTypes, setServiceTypes] = useState([]);
	const [waitingCount, setWaitingCount] = useState(0);

	const navigate = useNavigate();

	// ─── Длина очереди (ожидающие талоны по видам услуг менеджера) ─────────────
	const fetchQueueCount = useCallback(async () => {
		try {
			const res = await activeTicketService.getManagerQueueCount();
			setWaitingCount(res.data?.waitingCount ?? 0);
		} catch (e) {
			console.error('Failed to fetch queue count', e);
		}
	}, []);

	// ─── Обновить талоны менеджера (текущий + история) ─────────────────────────
	const refreshTickets = useCallback(async () => {
		try {
			const response = await activeTicketService.getAllManagerTickets();
			setTickets(response.data);
		} catch (e) {
			console.error('Failed to refresh tickets', e);
		}
	}, []);

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const [response, userInfoResponse] = await Promise.all([
				activeTicketService.getAllManagerTickets(),
				AuthService.getCurrentUser()
			]);
			setUserInfo(userInfoResponse);
			setTickets(response.data);
			setErrorMessage('');
			setInfoMessage('');
		} catch (e) {
			console.error('Failed to fetch tickets', e);
			setErrorMessage('Не удалось загрузить талоны');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchData();
		fetchQueueCount();
		serviceTypeService.getAllActive()
			.then(res => setServiceTypes(res.data || []))
			.catch(() => {});
	}, [fetchData, fetchQueueCount]);

	// ─── Реальное время: любое событие очереди → пересчитать счётчик ───────────
	// Талоны менеджера тоже обновляем (вызов/завершение могли прийти от системы).
	const handleWsEvent = useCallback(() => {
		fetchQueueCount();
		refreshTickets();
	}, [fetchQueueCount, refreshTickets]);

	const { status: wsStatus } = useWebSocket(QUEUE_TOPIC, handleWsEvent);

	// Полная ре-синхронизация при восстановлении WS-соединения
	const prevStatusRef = useRef(wsStatus);
	useEffect(() => {
		const prev = prevStatusRef.current;
		prevStatusRef.current = wsStatus;
		if (prev === WS_STATUS.RECONNECTING && wsStatus === WS_STATUS.CONNECTED) {
			fetchQueueCount();
			refreshTickets();
		}
	}, [wsStatus, fetchQueueCount, refreshTickets]);

	// Страховочный периодический ре-синк счётчика (на случай half-open WS)
	useEffect(() => {
		const id = setInterval(fetchQueueCount, RESYNC_INTERVAL_MS);
		return () => clearInterval(id);
	}, [fetchQueueCount]);

	const callNextTicket = async () => {
		setInfoMessage('');
		setErrorMessage('');
		try {
			const res = await activeTicketService.callNextTicket();
			// Возвращаем данные вызванного талона для голосового объявления
			return { ok: true, ticket: res.data };
		} catch (e) {
			console.error('Failed to call next ticket', e);
			const http = e?.response?.status;
			const statusStr = e?.response?.data?.status;
			const code = e?.response?.data?.errorCode;
			const apiMsg = e?.response?.data?.message;

			if (http === 404 || statusStr === 'NOT_FOUND' || code === 'OBJECT_NOT_FOUND') {
				setInfoMessage('Нет ожидающих талонов для вызова');
				return { ok: false };
			}
			setErrorMessage(apiMsg || 'Не удалось позвать следующий талон');
			return { ok: false };
		} finally {
			// Обновляем список и счётчик независимо от результата
			await refreshTickets();
			fetchQueueCount();
		}
	};

	const completeTicket = async () => {
		setInfoMessage('');
		setErrorMessage('');
		try {
			const res = await activeTicketService.completeTicket();
			return { ok: true, ticket: res.data };
		} catch (e) {
			const apiMsg = e?.response?.data?.message;
			setErrorMessage(apiMsg || 'Не удалось завершить обслуживание');
			return { ok: false };
		} finally {
			await refreshTickets();
			fetchQueueCount();
		}
	};

	const repeatCall = async () => {
		setInfoMessage('');
		setErrorMessage('');
		try {
			await activeTicketService.repeatCall();
			return { ok: true };
		} catch (e) {
			const apiMsg = e?.response?.data?.message;
			setErrorMessage(apiMsg || 'Не удалось повторить вызов');
			return { ok: false };
		}
	};

	const skipTicket = async () => {
		setInfoMessage('');
		setErrorMessage('');
		try {
			const res = await activeTicketService.skipTicket();
			return { ok: true, ticket: res.data };
		} catch (e) {
			const apiMsg = e?.response?.data?.message;
			setErrorMessage(apiMsg || 'Не удалось пропустить талон');
			return { ok: false };
		} finally {
			await refreshTickets();
			fetchQueueCount();
		}
	};

	const redirectTicket = async (serviceTypeId) => {
		setInfoMessage('');
		setErrorMessage('');
		try {
			const res = await activeTicketService.redirectTicket(serviceTypeId);
			return { ok: true, ticket: res.data };
		} catch (e) {
			const apiMsg = e?.response?.data?.message;
			setErrorMessage(apiMsg || '');
			return { ok: false };
		} finally {
			await refreshTickets();
			fetchQueueCount();
		}
	};

	return {
		tickets,
		userInfo,
		loading,
		errorMessage,
		infoMessage,
		serviceTypes,
		waitingCount,
		wsStatus,
		callNextTicket,
		completeTicket,
		repeatCall,
		skipTicket,
		redirectTicket,
		navigate
	};
};
